import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api.js';
import { useI18n } from '../i18n/index.jsx';
import { useLive } from '../live.jsx';
import './whatsapp.css';

// WhatsApp, Part 1 (migration 043): wa.me links with a pre-filled message.
// No API: we only know a link was clicked (onOpen, e.g. to log it on the
// customer's timeline), never the chat itself.

export const waDigits = (n) => String(n || '').replace(/\D/g, '');
// 10-digit North American numbers (how the site stores phones) get +1.
export function waHref(number, text) {
  let d = waDigits(number);
  if (d.length === 10) d = `1${d}`;
  return `https://wa.me/${d}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}

export function WaIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="wa-icon">
      <path fill="currentColor" d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 8.24 8.25c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29z" />
    </svg>
  );
}

// A green "WhatsApp" button (a link to wa.me). onOpen runs on click.
export function WhatsAppButton({ number, text, label, className = '', onOpen, small = false }) {
  const { t } = useI18n();
  if (!waDigits(number)) return null;
  return (
    <a className={`btn btn-wa${small ? ' btn-sm' : ''} ${className}`} href={waHref(number, text)} target="_blank" rel="noreferrer noopener" onClick={() => onOpen?.()}>
      <WaIcon size={small ? 16 : 18} /> <span>{label || t('wa.button')}</span>
    </a>
  );
}

// PME Trade Canada's number and floating-button settings (Admin › WhatsApp).
let cache = null; let pending = null;
export function useWhatsAppPublic() {
  const [s, setS] = useState(cache);
  const [tick, setTick] = useState(0);
  useLive(['whatsapp'], () => { cache = null; setTick((x) => x + 1); });
  useEffect(() => {
    if (cache && !tick) return;
    pending = pending || api('/whatsapp/public').then((d) => { cache = d; return d; }).catch(() => ({ number: null, floatEnabled: false })).finally(() => { pending = null; });
    pending.then(setS);
  }, [tick]);
  return s;
}

// The floating button on the public site (not in My account / Admin).
export function WhatsAppFloat() {
  const { t, lang } = useI18n();
  const s = useWhatsAppPublic();
  const { pathname } = useLocation();
  if (!s?.floatEnabled || !s.number || /^\/(account|admin|d|showcase)(\/|$)/.test(pathname)) return null;
  const text = lang === 'en' ? s.messageEn : s.messageFr;
  return (
    <a className="wa-float" href={waHref(s.number, text)} target="_blank" rel="noreferrer noopener" aria-label={t('wa.floatLabel')} title={t('wa.floatLabel')}>
      <WaIcon size={30} />
    </a>
  );
}
